import React from 'react';
import { Instagram, Linkedin, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DASHBOARD_SIGNIN_URL, INSTAGRAM_URL, LINKEDIN_URL, SHOPIFY_APP_URL, TRACKING_URL, YOUTUBE_URL } from '@/config/links';
import { ROUTES } from '@/config/routes';

const FooterColumn = ({ title, links }) => (
  <div>
    <h3 className="text-base font-bold mb-4 text-white">{title}</h3>
    <ul className="space-y-3">
      {links.map((item) => (
        <li key={`${title}-${item.label}`}>
          {item.external ? (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-700 transition-colors text-sm"
            >
              {item.label}
            </a>
          ) : (
            <Link to={item.href} className="text-gray-400 hover:text-blue-700 transition-colors text-sm">
              {item.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const year = new Date().getFullYear();
  const footerColumns = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '/#features' },
        { label: 'Services', href: '/#services' },
        { label: 'Pricing', href: ROUTES.PRICING }
      ]
    },
    {
      title: 'Solutions',
      links: [
        { label: 'Track Order', href: TRACKING_URL },
        { label: 'Check Pincode', href: '/serviceable-pincode' },
        { label: 'Print Label', href: '/print-label' },
        { label: 'COD Service', href: '/cod' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { label: 'Blogs', href: ROUTES.BLOGS },
        { label: 'Documentation', href: ROUTES.RESOURCES }
      ]
    },
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: ROUTES.HELP_CENTER },
        { label: 'FAQ', href: '/#faq' },
        { label: 'Contact Us', href: ROUTES.CONTACT }
      ]
    },
    {
      title: 'Company',
      links: [
        { label: 'About Us', href: ROUTES.ABOUT },
        { label: 'Login', href: DASHBOARD_SIGNIN_URL, external: true }
      ]
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms & Conditions', href: ROUTES.TERMS },
        { label: 'Shipping Policy', href: '/shipping-policy' },
        { label: 'Return & Refunds', href: '/return-refund' },
        { label: 'Privacy Policy', href: '/policy' }
      ]
    }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white overflow-hidden">
      {/* Decorative Background with Animation */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.02) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-700/5 to-transparent rounded-full blur-3xl animate-pulse-slow"></div>
      </div>

      <div className="relative">
        {/* Top decorative line */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-700 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-12">
            {/* Company Info */}
            <div className="max-w-3xl">
              <Link to="/" className="inline-block mb-6 group">
                <div className="bg-white rounded-lg p-3 inline-flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/logo.jpg`}
                    alt="Boxd Logistics"
                    className="h-10 w-auto object-contain"
                  />
                </div>
              </Link>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Empowering your eCommerce deliveries with precision, speed, and reliability. Your trusted partner for seamless logistics solutions.
              </p>
            </div>
          </div>

          <div className="border-t border-blue-900 pt-10 pb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-10">
              {footerColumns.map((column) => (
                <FooterColumn key={column.title} title={column.title} links={column.links} />
              ))}
            </div>
          </div>

          {/* Integrations strip */}
          <div className="py-6 border-t border-blue-900/60">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Integrations
              </h3>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {[
                  { name: 'Shopify', logo: 'shopify.svg', href: SHOPIFY_APP_URL },
                  { name: 'WooCommerce', logo: 'woocommerce.svg' },
                  { name: 'Amazon', logo: 'amazon.svg' },
                  { name: 'EasyEcom', logo: 'easyecom.svg' },
                  { name: 'Unicommerce', logo: 'unicommerce.svg' },
                ].map((platform) => {
                  const content = (
                    <>
                      <img
                        src={`${process.env.PUBLIC_URL || ''}/logos/${platform.logo}`}
                        alt={platform.name}
                        className="h-4 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="text-xs text-gray-400 group-hover:text-gray-200 transition-colors">
                        {platform.name}
                      </span>
                    </>
                  );
                  const className =
                    'group inline-flex items-center gap-2 rounded-md bg-white/5 hover:bg-white/10 px-3 py-1.5 transition-colors';
                  return platform.href ? (
                    <a
                      key={platform.name}
                      href={platform.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                      title={`${platform.name} — View on App Store`}
                    >
                      {content}
                    </a>
                  ) : (
                    <span key={platform.name} className={className} title={platform.name}>
                      {content}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-blue-900/60">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © {year} Boxd Logistics. All rights reserved.
              </p>
              <div className="flex items-center space-x-3">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-blue-900 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-all transform hover:scale-105" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-blue-900 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-all transform hover:scale-105" aria-label="YouTube">
                  <Youtube className="w-4 h-4" />
                </a>
                <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-blue-900 hover:bg-emerald-600 rounded-lg flex items-center justify-center transition-all transform hover:scale-105" aria-label="LinkedIn">
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;